import React, { useState } from 'react';
import { Activity, AlertCircle, Eye, EyeOff, Lock, Mail, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import EULAModal from './modals/EULAModal';
import ContactModal from './modals/ContactModal';
import PrivacyModal from './modals/PrivacyModal';
import TermsModal from './modals/TermsModal';
import ReCAPTCHA from 'react-google-recaptcha';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userId, setUserId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [acceptedEula, setAcceptedEula] = useState(false);
  const [showEula, setShowEula] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const navigate = useNavigate();
  const { theme } = useTheme();

  const { signup, login, resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        // Validate userId format
        const userIdRegex = /^[a-zA-Z0-9_-]+$/;
        if (!userIdRegex.test(userId)) {
          throw new Error('User ID can only contain letters, numbers, underscores, and hyphens');
        }
        // Verify captcha for signup
        if (!captchaToken) {
          throw new Error('Please complete the CAPTCHA verification');
        }
        if (!acceptedEula) {
          throw new Error('Please accept the Terms and EULA to continue');
        }
        await signup(email, password, userId);
        setSuccess('Account created successfully! You can now log in.');
        setIsLogin(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to authenticate');
    }

    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await resetPassword(email);
      setSuccess('Password reset email sent! Check your inbox.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    }
    setLoading(false);
  };

  return (
    <main className={`min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 ${
      theme === 'dark' ? 'bg-navy-900' : 'bg-[#EAEDED]'
    }`}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Activity className="h-12 w-12 text-orange-500" />
        </div>
        <h2 className={`mt-6 text-center text-3xl font-extrabold text-orange-500`}>
          {isLogin ? 'Sign in to your account' : 'Create a new account'}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className={`py-8 px-4 shadow-xl sm:rounded-lg sm:px-10 border ${
          theme === 'dark' 
            ? 'bg-navy-800 border-navy-700' 
            : 'bg-white border-gray-200'
        }`}>
          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500 rounded-md p-3 flex items-center space-x-2 text-red-400">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="mb-4 bg-green-500/10 border border-green-500 rounded-md p-3 flex items-center space-x-2 text-green-400">
              <AlertCircle size={20} />
              <span>{success}</span>
            </div>
          )}
          <form className="login-form space-y-6" onSubmit={handleSubmit}>
            {!isLogin && (
              <div>
                <label htmlFor="userId" className={`block text-sm font-medium text-orange-500`}>
                  User ID
                </label>
                <div className="mt-1 relative bg-[#F5F7FF] rounded-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className={`h-5 w-5 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                  </div>
                  <input
                    id="userId"
                    name="userId"
                    type="text"
                    required
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    className="appearance-none block w-full pl-10 pr-3 py-2 rounded-md bg-[#F5F7FF] border-0 text-gray-900 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    placeholder="Choose a unique user ID"
                  />
                </div>
                <p className={`mt-1 text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  This will be your unique identifier. Only letters, numbers, underscores, and hyphens allowed.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="email" className={`block text-sm font-medium text-orange-500`}>
                Email address
              </label>
              <div className="mt-1 relative bg-[#F5F7FF] rounded-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`h-5 w-5 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-2 rounded-md bg-[#F5F7FF] border-0 text-gray-900 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className={`block text-sm font-medium text-orange-500`}>
                  Password
                </label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    className="text-sm font-medium text-orange-500 hover:text-orange-400"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <div className="relative flex-grow">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className={`h-5 w-5 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full pl-10 pr-3 py-2 rounded-md bg-[#F5F7FF] border-0 text-gray-900 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    placeholder="••••••••"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`p-2 rounded-md hover:bg-gray-100 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <>
                <div className="flex justify-center">
                  <ReCAPTCHA
                    sitekey="6LcO2-QqAAAAAG2Is5Q-setUfh08le9u9CeZbnEA"
                    onChange={(token) => setCaptchaToken(token)}
                  />
                </div>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="eula"
                      name="eula"
                      type="checkbox"
                      checked={acceptedEula}
                      onChange={(e) => setAcceptedEula(e.target.checked)}
                      className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="eula" className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      I agree to the{' '}
                      <button
                        type="button"
                        className="text-orange-500 hover:text-orange-400 font-medium"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowEula(true);
                        }}
                      >
                        Terms and EULA
                      </button>
                    </label>
                  </div>
                </div>
              </>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Sign Up'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${
                  theme === 'dark' 
                    ? 'bg-navy-800 text-gray-400' 
                    : 'bg-white text-gray-500'
                }`}>
                  {isLogin ? 'New to Show Your Bits?' : 'Already have an account?'}
                </span>
              </div>
            </div>

            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setSuccess('');
                setUserId('');
              }}
              className="mt-4 w-full text-center text-sm text-orange-500 hover:text-orange-400 transition-colors"
            >
              {isLogin ? 'Create an account' : 'Sign in to your account'}
            </button>
          </div>

          <EULAModal
            isOpen={showEula}
            onClose={() => setShowEula(false)}
          />
        </div>
      </div>
      <div className="mt-8 text-center text-sm">
        <button
          onClick={() => setShowPrivacy(true)}
          className="text-gray-500 hover:text-gray-700 mx-2"
        >
          Privacy Policy
        </button>
        <span className="text-gray-400">•</span>
        <button
          onClick={() => setShowTerms(true)}
          className="text-gray-500 hover:text-gray-700 mx-2"
        >
          Terms of Service
        </button>
        <span className="text-gray-400">•</span>
        <button
          onClick={() => setShowContact(true)}
          className="text-gray-500 hover:text-gray-700 mx-2"
        >
          Contact Us
        </button>
      </div>
      <ContactModal
        isOpen={showContact}
        onClose={() => setShowContact(false)}
      />
      <PrivacyModal
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      />
      <TermsModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
      />
    </main>
  );
}